import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn_old(...inputs) {
    const raw = clsx(inputs).split(/\s+/);

    // Xử lý từng class
    const stripped = raw.map(c => {
        // Tách prefix (sm:, md:, hover:, focus:, v.v.)
        const parts = c.split(":");
        const base = parts.pop(); // lấy class gốc cuối cùng
        const prefix = parts.length ? parts.join(":") + ":" : "";

        // Bỏ "tw-" ở class gốc
        const cleaned = base.replace(/^tw-/, "");

        return prefix + cleaned;
    });

    // Merge
    const merged = twMerge(stripped);

    // Thêm lại "tw-" nhưng chỉ cho phần gốc, không thêm vào prefix
    return merged
        .split(/\s+/)
        .map(c => {
            const parts = c.split(":");
            const base = parts.pop();
            const prefix = parts.length ? parts.join(":") + ":" : "";
            return prefix + "tw-" + base;
        })
        .join(" ");
}


export function cn(...inputs) {
    const raw = clsx(inputs).split(/\s+/);

    // Theo dõi class nào cần giữ "tw-"
    const classesToProcess = raw.map(c => {
        const hasTwPrefix = c.includes("tw-");
        return { original: c, needsTwPrefix: hasTwPrefix };
    });

    // Loại bỏ "tw-" tạm thời cho twMerge
    const stripped = classesToProcess.map(({ original, needsTwPrefix }) => {
        const parts = original.split(":");
        const base = parts.pop();
        const prefix = parts.length ? parts.join(":") + ":" : "";

        const cleaned = needsTwPrefix ? base.replace(/^tw-/, "") : base;
        return prefix + cleaned;
    });

    const merged = twMerge(stripped);

    // Thêm "tw-" lại chỉ cho class cần thiết
    return merged
        .split(/\s+/)
        .map(c => {
            const parts = c.split(":");
            const base = parts.pop();
            const prefix = parts.length ? parts.join(":") + ":" : "";

            // Kiểm tra class gốc nào tương ứng để xác định có cần "tw-" không
            const needsTwPrefix = classesToProcess.some(({ original, needsTwPrefix }) => {
                if (!needsTwPrefix) return false;
                const originalBase = original.split(":").pop().replace(/^tw-/, "");
                return originalBase === base;
            });

            return prefix + (needsTwPrefix ? "tw-" + base : base);
        })
        .join(" ");
}


// console.log(cn("tw-max-w-full", "tw-max-w-[calc(100vw-20px)]"));
// 👉 "tw-max-w-[calc(100vw-20px)]"


export const isValidUrl = (string) => {
    try {
        new URL(string);
        return true;
    } catch (_) {
        return false;
    }
};

export const toTitleCase = (str) =>
    str.replace(/\w\S*/g, (txt) =>
        txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
    );

export const capitalizeFirstLetter = (str) =>
    typeof str === 'string' && str.length > 0
        ? str.charAt(0).toUpperCase() + str.slice(1)
        : str;


export function isImageUrlExpiredOrInvalid(url) {
    if (!url) return true;

    try {
        const parsedUrl = new URL(url);
        const expires = parsedUrl.searchParams.get("X-Amz-Expires");
        const amzDate = parsedUrl.searchParams.get("X-Amz-Date");

        if (!expires || !amzDate) return true;

        const expiresIn = parseInt(expires, 10);
        const generatedAt = new Date(
            amzDate.slice(0, 4) + '-' +
            amzDate.slice(4, 6) + '-' +
            amzDate.slice(6, 8) + 'T' +
            amzDate.slice(9, 11) + ':' +
            amzDate.slice(11, 13) + ':' +
            amzDate.slice(13, 15) + 'Z'
        );
        const expiryDate = new Date(generatedAt.getTime() + expiresIn * 1000);
        // console.log({ generatedAt, expiryDate})

        return Date.now() > expiryDate.getTime();
    } catch (e) {
        return true; // lỗi format URL hoặc thiếu param
    }
}

export const waitForElement = (selector, timeout = 5000) => {
    return new Promise((resolve, reject) => {
        const interval = 100;
        let elapsed = 0;
        let lastBox = null;

        const check = () => {
            const el = document.querySelector(selector);

            if (el && el.offsetParent !== null) {
                const box = el.getBoundingClientRect();

                // Kiểm tra xem element có render với kích thước hợp lý chưa
                const hasSize = box.width > 0 && box.height > 0;
                const isVisible = box.top >= 0 && box.left >= 0;

                // Nếu lần trước chưa có box hoặc vị trí còn thay đổi, tiếp tục chờ
                const isStable = !lastBox || (
                    lastBox.top === box.top &&
                    lastBox.left === box.left &&
                    lastBox.width === box.width &&
                    lastBox.height === box.height
                );

                lastBox = box;

                if (hasSize && isVisible && isStable) {
                    resolve(el);
                    return;
                }
            }

            if (elapsed >= timeout) {
                reject(`Element ${selector} not visible or stable after ${timeout}ms`);
            } else {
                elapsed += interval;
                setTimeout(check, interval);
            }
        };

        check();
    });
};


export const validateSendCollaborationRequestDetails = ({
    description,
    setDescriptionError,
    price,
    setPriceError,
    object,
    setObjectError,
    deliveryTime,
    setDateError,
    setFieldErrors
}) => {
    let errors = new Map();

    if (description.length < 21) {
        setDescriptionError(true);
        errors.set(
            "description",
            "The description needs to be more than 20 characters!"
        );
    }
    if (price.length < 1) {
        setPriceError(true);
        errors.set("price", "Please enter a valid offering price");
    }
    if (object.length < 1) {
        setObjectError(true);
        errors.set("object", "Please select an object");
    }

    if (deliveryTime.length < 10) {
        setDateError(true);
        errors.set("delivery_time", "Please enter a delivery time");
    } else {
        const selectedDate = new Date(deliveryTime);
        const now = new Date();

        if (selectedDate <= now) {
            setDateError(true);
            errors.set("delivery_time", "The delivery time must be a date after now.");
        }
    }

    setFieldErrors(errors);

    return errors.size === 0;
};

export function isInAppBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";

  const patterns = [
    /FBAN/i,       // Facebook app
    /FBAV/i,       // Facebook app
    /Instagram/i,  // Instagram
    /Line/i,       // Line app
    /Twitter/i,    // Twitter/X
    /TikTok/i,     // TikTok
    /Zalo/i        // Zalo
  ];

  return patterns.some((pattern) => pattern.test(ua));
}